/**
 * @file billEdit.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-30
 * @description Which edit a bill gets, and what an edit of a bill already in
 *              Xero changes there. Mirrors utils/billPostedEdit.editContext on
 *              the server (GET /api/bills/:id carries it as `edit_context`),
 *              for the list's Edit action, where rows carry no context; the
 *              server has the last word on save.
 *
 *   local        — not in Xero yet: the ordinary edit
 *   status_only  — entered here, in Xero, bill editing switched off
 *   xero_draft   — keyed in Xero, a draft, switched off
 *   patch        — in Xero, switched on: only what is changed is sent to Xero
 *   blocked      — none (its PDF, notes and POs still change in the bill window)
 *
 * 1.1.0 (2026-10-30, Cam — "We should be able to edit bills and credits like
 * we can invoices and credits locally and sync the change with Xero, unless
 * payments have been allocated or the document is behind a Xero accounting
 * lock"): a SUPPLIER CREDIT in Xero is edited as a bill is (patch), refused
 * only while any of it is allocated or refunded (and, as a bill, voided, not
 * sterling, the permission); an overpayment / prepayment stays refused.
 * describeBillEditInXero says a credit's number and a change of what it is
 * for in the credit's words.
 */

import { billInXero } from './billStatus';
import { formatQuantity } from './numberFormatters';

const lower = (v) => String(v || '').toLowerCase();
const isDraft = (status) => ['draft', 'submitted'].includes(lower(status));

/**
 * @param {object} bill — status, source, xero_bill_id, transaction_type, is_credit, amount_paid, currency_code
 * @param {{ switchOn?: boolean, canEditPosted?: boolean }} opts
 * @returns {{ mode: string, editable: boolean, posted: boolean, needs_reason: boolean, block: string|null }}
 */
export function billEditMode(bill, { switchOn = false, canEditPosted = false } = {}) {
  const out = (mode, extra = {}) => ({
    mode, editable: ['local', 'xero_draft', 'patch'].includes(mode), posted: false, needs_reason: false, block: null, ...extra,
  });
  if (!bill) return out('blocked', { block: 'No bill' });
  const tt = String(bill.transaction_type || 'BILL').toUpperCase();
  const credit = tt === 'CREDIT';
  const noun = credit ? 'supplier credit' : 'bill';
  const st = lower(bill.status);
  if (st === 'voided' || st === 'deleted') return out('blocked', { block: `This ${noun} is ${st}` });
  if (!billInXero(bill)) {
    if (bill.source === 'local') return out('local');
    return st === 'draft' ? out('xero_draft') : out('blocked', { block: `This ${noun} came from Xero — edit it in Xero` });
  }
  if (!switchOn) {
    if (bill.source === 'local') {
      return out('status_only', { block: credit
        ? 'This supplier credit is in Xero: change its status with Status; what it relates to, its PDF and notes in the credit window (editing bills in Xero is switched off)'
        : 'This bill is in Xero: change its status with Status; its PDF, notes and POs in the bill window (editing bills in Xero is switched off)' });
    }
    return st === 'draft' ? out('xero_draft') : out('blocked', { block: `This ${noun} came from Xero — edit it in Xero (its PDF, notes and ${credit ? 'what it relates to' : 'POs'} can be changed in the ${credit ? 'credit' : 'bill'} window)` });
  }
  if (tt === 'OVERPAYMENT' || tt === 'PREPAYMENT') return out('blocked', { block: `An ${tt.toLowerCase()} is not edited here` });
  if (credit) {
    // Any of it used (allocated to a bill or refunded): taken off first.
    if (st === 'paid' || Math.abs(parseFloat(bill.amount_paid || 0)) > 0.005 || Number(bill.credit_allocation_count || 0) > 0) {
      return out('blocked', { block: 'Some of this credit is allocated to a bill or refunded — take the allocation off first (Allocate to bills…), then edit it' });
    }
  } else if (bill.is_credit) {
    return out('blocked', { block: 'This credit is not edited here' });
  } else if (st === 'paid' || Math.abs(parseFloat(bill.amount_paid || 0)) > 0.005) {
    return out('blocked', { block: 'It has a payment against it in Xero — its PDF, notes and POs can still be changed in the bill window' });
  }
  if (String(bill.currency_code || 'GBP').toUpperCase() !== 'GBP') return out('blocked', { block: `Only sterling ${noun}s can be edited here` });
  const posted = !isDraft(bill.status);
  if (posted && !canEditPosted) {
    return out('blocked', { posted, block: `Changing a ${noun} approved in Xero needs the 'Edit posted documents' permission` });
  }
  return out('patch', { posted, needs_reason: posted });
}

const n4 = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? Math.round(x * 10000) / 10000 : 0;
};
const t = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
const money = (v) => `£${(parseFloat(v) || 0).toFixed(2)}`;
/** A quantity in the words: 2dp, more when it has them ("4.00", "4.0125"). */
const qty = (v) => formatQuantity(n4(v));

/**
 * What an edit of a bill in Xero changes THERE, in words, for the reason
 * window (PostedEditDialog). Our own fields (notes, our reference on a bill
 * entered here, additives, PO links) are not listed — they never reach Xero.
 *
 * A supplier credit (`transaction_type` 'CREDIT' on `before`): its number is
 * its credit number, it has no due date, and a change of what it is for
 * (`credit_kind` on both) says what that does to its item lines in Xero.
 *
 * @param {object} before — { bill_date, due_date, supplier_reference, reference, source, transaction_type, credit_kind,
 *                            lines: [{ id, description, item_code, quantity, unit_amount, account_code, tax_type }] }
 * @param {object} after  — the same header fields, lines as the save's payload (bill_line_id …)
 * @returns {string[]}
 */
const CREDIT_KIND_WORDS = { return: 'Goods returned', price: 'Price correction' };

export function describeBillEditInXero(before, after) {
  const out = [];
  if (!before || !after) return out;
  const credit = String(before.transaction_type || 'BILL').toUpperCase() === 'CREDIT';
  const day = (d) => (d ? String(d).slice(0, 10) : '');
  if (day(before.bill_date) !== day(after.bill_date)) out.push(`date ${day(before.bill_date) || '(none)'} → ${day(after.bill_date) || '(none)'}`);
  if (!credit && day(before.due_date) !== day(after.due_date)) out.push(`due date ${day(before.due_date) || '(none)'} → ${day(after.due_date) || '(none)'}`);
  if (t(before.supplier_reference) !== t(after.supplier_reference)) {
    out.push(`${credit ? 'credit number' : "supplier's invoice number"} ${t(before.supplier_reference) || '(none)'} → ${t(after.supplier_reference) || '(none)'}`);
  }
  // What it is for reaches Xero only through its item lines (with or without
  // their codes); with none it is ours alone and is not listed.
  if (credit && after.credit_kind && after.credit_kind !== before.credit_kind && (after.lines || []).some(l => t(l.item_code))) {
    out.push(`what it is for ${CREDIT_KIND_WORDS[before.credit_kind] || 'not set'} → ${CREDIT_KIND_WORDS[after.credit_kind]}`
      + (after.credit_kind === 'price'
        ? ' — its item lines go to Xero without their item codes (stock not touched)'
        : ' — its item lines go to Xero with their item codes (the stock comes off)'));
  }
  if (before.source !== 'local' && t(before.reference) !== t(after.reference)) out.push(`reference ${t(before.reference) || '(none)'} → ${t(after.reference) || '(none)'}`);
  const old = new Map((before.lines || []).map(l => [Number(l.id), l]));
  const seen = new Set();
  (after.lines || []).forEach((a, i) => {
    const b = a.bill_line_id != null ? old.get(Number(a.bill_line_id)) : null;
    if (!b || seen.has(Number(b.id))) {
      out.push(`line ${i + 1} added (${t(a.description).slice(0, 50) || 'no description'} × ${qty(a.quantity)} @ ${money(a.unit_amount)})`);
      return;
    }
    seen.add(Number(b.id));
    const bits = [];
    if (t(b.description) !== t(a.description)) bits.push('description');
    if (t(b.item_code).toUpperCase() !== t(a.item_code).toUpperCase()) bits.push(`item ${t(b.item_code) || '(none)'} → ${t(a.item_code) || '(none)'}`);
    if (n4(b.quantity) !== n4(a.quantity)) bits.push(`quantity ${qty(b.quantity)} → ${qty(a.quantity)}`);
    if (n4(b.unit_amount) !== n4(a.unit_amount)) bits.push(`price ${money(b.unit_amount)} → ${money(a.unit_amount)}`);
    if (t(b.account_code).toUpperCase() !== t(a.account_code).toUpperCase()) bits.push(`account ${t(b.account_code) || '(none)'} → ${t(a.account_code) || '(none)'}`);
    if ((t(b.tax_type) || 'NONE').toUpperCase() !== (t(a.tax_type) || 'NONE').toUpperCase()) bits.push('tax');
    if (bits.length) out.push(`line ${i + 1}: ${bits.join(', ')}`);
  });
  for (const b of before.lines || []) {
    if (!seen.has(Number(b.id))) out.push(`removed "${t(b.description).slice(0, 50)}"`);
  }
  return out;
}
